/// <reference lib="dom" />
/**
 * web/audioAssets.ts
 * Static registry of every bundled audio file.
 *
 * Mirrors `web/assets.ts`: each file is a static `import`, so Vite fingerprints
 * it, emits it into `dist-vite/`, and hands back a local URL. Nothing is
 * fetched from a remote origin — see ADR-005.
 *
 * `web/__tests__/audioAssets.test.ts` compares these tables against the actual
 * directory listing, so renaming or deleting a file breaks the test rather
 * than failing silently at runtime.
 *
 * @module web/audioAssets
 */

import type { AudioSourceRegistry, MusicId, SfxId } from '@src/game/types/audio';
import { registerAudioSources } from '@src/game/services/audioManager';

import bossDefeatUrl from '../src/assets/audio/sfx/boss-defeat.m4a';
import bossHurtUrl from '../src/assets/audio/sfx/boss-hurt.m4a';
import bossPhaseUrl from '../src/assets/audio/sfx/boss-phase.m4a';
import checkpointUrl from '../src/assets/audio/sfx/checkpoint.m4a';
import enemyDefeatUrl from '../src/assets/audio/sfx/enemy-defeat.m4a';
import enemyHurtUrl from '../src/assets/audio/sfx/enemy-hurt.m4a';
import heartCollectUrl from '../src/assets/audio/sfx/heart-collect.m4a';
import jumpUrl from '../src/assets/audio/sfx/jump.m4a';
import landUrl from '../src/assets/audio/sfx/land.m4a';
import menuCancelUrl from '../src/assets/audio/sfx/menu-cancel.m4a';
import menuConfirmUrl from '../src/assets/audio/sfx/menu-confirm.m4a';
import novaDeathUrl from '../src/assets/audio/sfx/nova-death.m4a';
import novaHurtUrl from '../src/assets/audio/sfx/nova-hurt.m4a';
import seedCollectUrl from '../src/assets/audio/sfx/seed-collect.m4a';
import shardCollectUrl from '../src/assets/audio/sfx/shard-collect.m4a';
import shardFireUrl from '../src/assets/audio/sfx/shard-fire.m4a';
import shardHitUrl from '../src/assets/audio/sfx/shard-hit.m4a';
import stompUrl from '../src/assets/audio/sfx/stomp.m4a';

import bossGaleRoosterUrl from '../src/assets/audio/music/boss-gale-rooster.m4a';
import world1SunmeadowUrl from '../src/assets/audio/music/world-1-sunmeadow.m4a';

/** Bundled URL for every sound effect, keyed by its filename stem. */
export const SFX_URLS: Readonly<Record<SfxId, string>> = {
  'boss-defeat': bossDefeatUrl,
  'boss-hurt': bossHurtUrl,
  'boss-phase': bossPhaseUrl,
  checkpoint: checkpointUrl,
  'enemy-defeat': enemyDefeatUrl,
  'enemy-hurt': enemyHurtUrl,
  'heart-collect': heartCollectUrl,
  jump: jumpUrl,
  land: landUrl,
  'menu-cancel': menuCancelUrl,
  'menu-confirm': menuConfirmUrl,
  'nova-death': novaDeathUrl,
  'nova-hurt': novaHurtUrl,
  'seed-collect': seedCollectUrl,
  'shard-collect': shardCollectUrl,
  'shard-fire': shardFireUrl,
  'shard-hit': shardHitUrl,
  stomp: stompUrl,
};

/** Bundled URL for every music track, keyed by its filename stem. */
export const MUSIC_URLS: Readonly<Record<MusicId, string>> = {
  'boss-gale-rooster': bossGaleRoosterUrl,
  'world-1-sunmeadow': world1SunmeadowUrl,
};

/** The full registry handed to the AudioManager at boot. */
export const AUDIO_SOURCES: AudioSourceRegistry = {
  sfx: SFX_URLS,
  music: MUSIC_URLS,
};

/** Hand every bundled audio URL to the AudioManager. Call once at boot. */
export function registerWebAudioSources(): void {
  registerAudioSources(AUDIO_SOURCES);
}
